import type { BillingSubscriptionPlan, BillingSubscriptionProduct } from '../types/billing';
import { subscriptionsForPlan } from './billingSubscriptionPlans';
import { formatCreditCount } from './usageCredits';

const ANALYSIS_CREDIT_ESTIMATE = {
  low: 11_573,
  high: 35_560,
} as const;

export function replaceBillingTranslationValues(
  template: string,
  values: Record<string, string>,
) {
  return Object.entries(values).reduce(
    (value, [key, replacement]) => value.replace(`{${key}}`, replacement),
    template,
  );
}

export function annualSubscriptionMonthsSaved(products: readonly BillingSubscriptionProduct[], plan: BillingSubscriptionPlan = 'pro') {
  const offers = subscriptionsForPlan(products, plan);
  const monthly = offers.find((product) => product.interval === 'monthly');
  const annual = offers.find((product) => product.interval === 'annual');
  if (!monthly || !annual || monthly.amount <= 0 || monthly.currency !== annual.currency) return 0;

  return Math.max(0, Math.round((monthly.amount * 12 - annual.amount) / monthly.amount));
}

export function formatBillingAmount(amountInMinorUnit: number, currency: string, language: 'en' | 'fr') {
  return new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountInMinorUnit / 100);
}

export function formatBillingCreditCount(credits: number, language: 'en' | 'fr', highlightQuantity = false) {
  if (credits < 1_000_000) {
    const label = formatCreditCount(credits, language);
    if (!highlightQuantity) return label;
    const quantity = new Intl.NumberFormat(language, { maximumFractionDigits: 0 }).format(Math.max(0, Math.floor(credits)));
    return label.replace(quantity, `<strong>${quantity}</strong>`);
  }
  const millions = credits / 1_000_000;
  const value = new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US', {
    maximumFractionDigits: 6,
  }).format(millions);
  const quantity = `${value} million${language === 'fr' && millions >= 2 ? 's' : ''}`;
  const formattedQuantity = highlightQuantity ? `<strong>${quantity}</strong>` : quantity;
  return `${formattedQuantity} ${language === 'fr' ? 'de crédits' : 'credits'}`;
}

export function estimateAnalysisRange(quotaCredits?: number) {
  if (!Number.isFinite(quotaCredits) || !quotaCredits || quotaCredits <= 0) return null;

  const min = Math.floor(quotaCredits / ANALYSIS_CREDIT_ESTIMATE.high);
  const max = Math.max(min, Math.floor(quotaCredits / ANALYSIS_CREDIT_ESTIMATE.low));

  return { min, max };
}
